import { Head, usePage, router } from "@inertiajs/react"
import { useState, useEffect, useRef } from "react"
import { FileBrowser } from "@/components/files/FileBrowser"

interface FileEntry {
  name: string
  path: string
  type: "file" | "folder"
  clientModified?: string
  serverModified?: string
  tags?: Record<string, string>
}

interface FilesProps {
  rootFolder: string
  currentPath: string
  browseRoot?: boolean
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

function pathToUrl(path: string): string {
  if (!path || path === "/") return "/files"
  const stripped = path.replace(/^\/+/, "")
  const encoded = stripped.split("/").map(segment => encodeURIComponent(segment)).join("/")
  return `/files/${encoded}`
}

export default function Files() {
  const { rootFolder, currentPath, browseRoot } = usePage().props as unknown as FilesProps
  const [entries, setEntries] = useState<FileEntry[]>([])
  const hasRedirected = useRef(false)

  useEffect(() => {
    // On first load, if at Dropbox root and user has a configured root folder,
    // redirect to the root folder path (replace so no back-history entry)
    if (!browseRoot && !hasRedirected.current && currentPath === "/" && rootFolder !== "/") {
      hasRedirected.current = true
      router.visit(pathToUrl(rootFolder), { replace: true })
      return
    }
    hasRedirected.current = true
    loadFolder(currentPath)
  }, [currentPath])

  async function loadFolder(path: string) {
    try {
      const res = await fetch(`/api/folders?path=${encodeURIComponent(path)}`, {
        headers: { "X-CSRF-Token": getCSRFToken() },
      })
      const data = await res.json()
      const folderEntries = (data.folders || []).map((f: { name: string; path: string }) => ({
        name: f.name,
        path: f.path,
        type: "folder" as const,
      }))
      const fileEntries = (data.files || []).map((f: { name: string; path: string; clientModified?: string; serverModified?: string }) => ({
        name: f.name,
        path: f.path,
        type: "file" as const,
        clientModified: f.clientModified,
        serverModified: f.serverModified,
      }))
      setEntries([...folderEntries, ...fileEntries])
    } catch {
      // ignore
    }
  }

  function handleNavigate(path: string) {
    router.visit(pathToUrl(path))
  }

  function handleRefresh() {
    loadFolder(currentPath)
  }

  return (
    <>
      <Head title="Files" />
      <FileBrowser
        rootFolder={rootFolder}
        currentPath={currentPath}
        entries={entries}
        onNavigate={handleNavigate}
        onRefresh={handleRefresh}
      />
    </>
  )
}
